"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import { getStrictContext } from "@/lib/get-strict-context"
import type { CurrentUser, PermissionCode } from "./permissions"
import { setAccessToken } from "./token-store"
import { refreshSession } from "./refresh"

type AuthContextValue = {
  user: CurrentUser | null
  permissions: Set<PermissionCode>
  isLoading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const [Provider, useAuth] = getStrictContext<AuthContextValue>("AuthProvider")

export { useAuth }

export function AuthProvider({
  children,
  initialUser = null,
  initialAccessToken = null,
}: {
  children: React.ReactNode
  /** Resolved by the root layout on the server; null means "look it up here". */
  initialUser?: CurrentUser | null
  /** The access JWT that came with `initialUser`, for the in-memory store. */
  initialAccessToken?: string | null
}) {
  const router = useRouter()
  const [user, setUser] = React.useState<CurrentUser | null>(initialUser)
  const [isLoading, setIsLoading] = React.useState(initialUser === null)

  const loadMe = React.useCallback(async () => {
    const fetchMe = () =>
      fetch("/api/auth/me", {
        credentials: "include",
        cache: "no-store",
      })

    let res = await fetchMe()
    if (res.status === 401) {
      // The access cookie expires after 5 min; recover with the refresh
      // cookie before treating the session as gone.
      const ok = await refreshSession()
      if (ok) res = await fetchMe()
    }
    if (res.ok) {
      const data = await res.json()
      setAccessToken(data.accessToken ?? null)
      setUser(data as CurrentUser)
    } else {
      setAccessToken(null)
      setUser(null)
    }
  }, [])

  React.useEffect(() => {
    if (initialUser) {
      // The store is what a cross-origin API host authenticates with (see
      // lib/api/api.ts `authHeaders`); /api/auth/me used to be what filled it.
      // Seeded in an effect, never during render: on the server the store is
      // module state shared by every request.
      setAccessToken(initialAccessToken)
      setIsLoading(false)
      return
    }
    loadMe().finally(() => setIsLoading(false))
  }, [initialUser, initialAccessToken, loadMe])

  const signIn = React.useCallback(
    async (email: string, password: string) => {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(
          data.detail ??
            (res.status === 401
              ? "Invalid email or password."
              : `Sign-in failed (${res.status})`),
        )
      }
      const data = await res.json()
      setAccessToken(data.accessToken ?? null)
      setUser(data.user as CurrentUser)
    },
    [],
  )

  const signOut = React.useCallback(async () => {
    await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    }).catch(() => null)
    setAccessToken(null)
    setUser(null)
    router.replace("/login")
  }, [router])

  const permissions = React.useMemo(
    () => new Set<PermissionCode>(user?.permissions ?? []),
    [user],
  )

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      permissions,
      isLoading,
      signIn,
      signOut,
      refresh: loadMe,
    }),
    [user, permissions, isLoading, signIn, signOut, loadMe],
  )

  return <Provider value={value}>{children}</Provider>
}
